import * as Sentry from "@sentry/react";
import { createBrowserRouter, Navigate } from "react-router-dom";
import { RedirectIfAuth } from "@/components/auth/RedirectIfAuth";
import { RequireAuth } from "@/components/auth/RequireAuth";
import { OrgLayout } from "@/layouts/OrgLayout";
import { AuthLayout } from "@/layouts/AuthLayout";
import { LoginPage } from "@/pages/auth/LoginPage";
import { RegisterPage } from "@/pages/auth/RegisterPage";
import { ForgotPasswordPage } from "@/pages/auth/ForgotPasswordPage";
import { ResetPasswordPage } from "@/pages/auth/ResetPasswordPage";
import { InviteErrorPage } from "@/pages/invite/InviteErrorPage";
import { InviteSignupPage } from "@/pages/invite/InviteSignupPage";
import { InviteAcceptPage } from "@/pages/invite/InviteAcceptPage";
import { CreateOrgPage } from "@/pages/onboarding/CreateOrgPage";
import { SelectOrgPage } from "@/pages/onboarding/SelectOrgPage";
import { RootRedirect } from "@/components/auth/RootRedirect";
import { AppLayout } from "@/layouts/AppLayout";
import { DashboardPage } from "@/pages/DashboardPage";
import { MembersPage } from "@/pages/MembersPage";
import { SettingsPage } from "@/pages/SettingsPage";
import { FormsPage } from "@/pages/settings/FormsPage";
import { ProjectsPage } from "@/pages/settings/ProjectsPage";
import { TeamsPage } from "@/pages/settings/TeamsPage";
import { TeamDetailPage } from "@/pages/settings/TeamDetailPage";
import { TimePage } from "@/pages/TimePage";
import { ErrorPage } from "@/pages/ErrorPage";

const sentryCreateBrowserRouter = Sentry.wrapCreateBrowserRouterV7(createBrowserRouter);

export const router = sentryCreateBrowserRouter([
  {
    errorElement: <ErrorPage />,
    children: [
      // Public: invitation pages (redirected from API server)
      {
        element: <AuthLayout />,
        children: [
          { path: "/invite/error", element: <InviteErrorPage /> },
          { path: "/invite/signup", element: <InviteSignupPage /> },
          { path: "/invite/accept", element: <InviteAcceptPage /> },
        ],
      },
      // Public: auth pages (redirect if already signed in)
      {
        element: <RedirectIfAuth />,
        children: [
          {
            element: <AuthLayout />,
            children: [
              { path: "/login", element: <LoginPage /> },
              { path: "/register", element: <RegisterPage /> },
              { path: "/forgot-password", element: <ForgotPasswordPage /> },
              { path: "/reset-password", element: <ResetPasswordPage /> },
            ],
          },
        ],
      },
      // Protected: requires authentication
      {
        element: <RequireAuth />,
        children: [
          {
            element: <AuthLayout />,
            children: [
              { path: "/onboarding/create-org", element: <CreateOrgPage /> },
              { path: "/onboarding/select-org", element: <SelectOrgPage /> },
            ],
          },
          // Org-scoped pages: /:orgId/:slug/*
          {
            path: "/:orgId/:slug",
            element: <OrgLayout />,
            children: [
              {
                element: <AppLayout />,
                children: [
                  { path: "dashboard", element: <DashboardPage /> },
                  { path: "time", element: <TimePage /> },
                  {
                    path: "settings",
                    element: <SettingsPage />,
                    children: [
                      { index: true, element: <Navigate to="members" replace /> },
                      { path: "forms", element: <FormsPage /> },
                      { path: "forms/:formId", element: <FormsPage /> },
                      { path: "projects", element: <ProjectsPage /> },
                      { path: "projects/:projectId", element: <ProjectsPage /> },
                      { path: "projects/:projectId/tasks/:taskId", element: <ProjectsPage /> },
                      { path: "members", element: <MembersPage /> },
                      { path: "teams", element: <TeamsPage /> },
                      { path: "teams/:teamId", element: <TeamDetailPage /> },
                    ],
                  },
                  { index: true, element: <Navigate to="dashboard" replace /> },
                ],
              },
            ],
          },
          // Root redirect: go to first org's dashboard
          { path: "/", element: <RootRedirect /> },
        ],
      },
    ],
  },
]);
